/**
 * Table de correspondance des comptes de paie (SCHÉMA UNIQUE des écritures de paie).
 *
 * Décision du chef de projet (option A, en attente de confirmation de l'expert-comptable DEC-05 / DEC-06) :
 * le code garde la nomenclature SYSCOHADA classique (422 net, 421 avances, 431x organismes,
 * 4421/4473 impôts, 661/663/664 charges). Le référentiel comptable v2 (onglet LIENS_PAIE) emploie
 * une nomenclature plus courte (661 / 664 / 421 / 425 / 431 / 447). L'écart est documenté ici,
 * ligne par ligne, pour être validé par l'expert-comptable ; si l'expert retient le référentiel v2,
 * il suffit de changer les valeurs de la table (par entreprise), sans modifier le code.
 *
 * Règles :
 * - une seule table pour TOUTES les écritures de paie (générateur, réconciliation, extourne, tests) ;
 * - chaque rôle porte un compte ; une entreprise peut surcharger un ou plusieurs rôles ;
 * - deux rôles qui n'appartiennent pas à la même famille du référentiel v2 ne peuvent pas partager
 *   le même compte (sinon la lecture « LIENS_PAIE » serait ambiguë).
 */

/** Rôles comptables d'une ligne d'écriture de paie. */
export const ROLES_COMPTE_PAIE = [
  'remuneration_base',
  'remuneration_sursalaire',
  'remuneration_indemnites',
  'remuneration_heures_sup',
  'indemnites_non_cotisables',
  'avantage_nature_contrepartie',
  'charge_cnss_patronale',
  'charge_cnamgs_patronale',
  'charge_taxes_patronales',
  'dette_cnss',
  'dette_cnamgs',
  'dette_irpp',
  'dette_tcs',
  'dette_css',
  'dette_cfp',
  'dette_fnh',
  'dette_fgtls',
  'dette_net',
  'dette_avances',
] as const;

export type RoleComptePaie = (typeof ROLES_COMPTE_PAIE)[number];

/** Famille de comptes du référentiel v2 (onglet LIENS_PAIE) à laquelle un rôle se rattache. */
export type FamilleReferentielV2 = '661' | '664' | '421' | '425' | '431' | '447' | 'hors_referentiel';

export type EcartReferentielV2 = 'identique' | 'sous_compte' | 'different' | 'hors_referentiel' | 'a_preciser';

export interface LigneCorrespondanceComptePaie {
  readonly role: RoleComptePaie;
  readonly libelle: string;
  /** Sens normal de la ligne dans l'écriture de paie. */
  readonly sens: 'debit' | 'credit';
  /** Compte par défaut du code (nomenclature SYSCOHADA classique). */
  readonly compteDefaut: string;
  /** Compte attendu par le référentiel v2 (onglet LIENS_PAIE), pour comparaison. */
  readonly compteReferentielV2: string;
  readonly famille: FamilleReferentielV2;
  readonly ecart: EcartReferentielV2;
  /** Classe du compte exigée (garde-fou de saisie de la surcharge). */
  readonly classe: 4 | 6 | 7;
  readonly commentaire: string;
}

export const CORRESPONDANCE_COMPTES_PAIE: readonly LigneCorrespondanceComptePaie[] = [
  {
    role: 'remuneration_base',
    libelle: 'Salaires de base',
    sens: 'debit',
    compteDefaut: '661100',
    compteReferentielV2: '661',
    famille: '661',
    ecart: 'sous_compte',
    classe: 6,
    commentaire: 'Sous-compte du 661 (brut imposable) : même famille.',
  },
  {
    role: 'remuneration_sursalaire',
    libelle: 'Sursalaires',
    sens: 'debit',
    compteDefaut: '661200',
    compteReferentielV2: '661',
    famille: '661',
    ecart: 'sous_compte',
    classe: 6,
    commentaire: 'Sous-compte du 661.',
  },
  {
    role: 'remuneration_indemnites',
    libelle: 'Indemnités et primes cotisables',
    sens: 'debit',
    compteDefaut: '661300',
    compteReferentielV2: '661',
    famille: '661',
    ecart: 'sous_compte',
    classe: 6,
    commentaire: 'Sous-compte du 661.',
  },
  {
    role: 'remuneration_heures_sup',
    libelle: 'Heures supplémentaires',
    sens: 'debit',
    compteDefaut: '661400',
    compteReferentielV2: '661',
    famille: '661',
    ecart: 'sous_compte',
    classe: 6,
    commentaire: 'Sous-compte du 661.',
  },
  {
    role: 'indemnites_non_cotisables',
    libelle: 'Indemnités non cotisables (transport exonéré, blanchissage, véhicule)',
    sens: 'debit',
    compteDefaut: '648100',
    compteReferentielV2: '661',
    famille: '661',
    ecart: 'hors_referentiel',
    classe: 6,
    commentaire:
      "Le référentiel v2 ne distingue pas les éléments non cotisables (661 = brut imposable). Le code les isole en 648 (autres charges de personnel). À valider par l'expert-comptable.",
  },
  {
    role: 'avantage_nature_contrepartie',
    libelle: 'Avantage en nature — contrepartie non monétaire',
    sens: 'credit',
    compteDefaut: '791000',
    compteReferentielV2: '(non prévu)',
    famille: 'hors_referentiel',
    ecart: 'hors_referentiel',
    classe: 7,
    commentaire:
      "L'avantage en nature est une charge (dans le brut) retirée du net en espèces ; la contrepartie est un transfert de charges (79). Hors référentiel v2.",
  },
  {
    role: 'charge_cnss_patronale',
    libelle: 'Cotisations CNSS patronales',
    sens: 'debit',
    compteDefaut: '663100',
    compteReferentielV2: '664',
    famille: '664',
    ecart: 'different',
    classe: 6,
    commentaire:
      "Le référentiel v2 porte les charges sociales patronales au 664 ; le code les ventile en 663 (cotisations sociales) et 664 (autres taxes sur salaires). L'ancien schéma racine (665) est supprimé.",
  },
  {
    role: 'charge_cnamgs_patronale',
    libelle: 'Cotisations CNAMGS patronales',
    sens: 'debit',
    compteDefaut: '663200',
    compteReferentielV2: '664',
    famille: '664',
    ecart: 'different',
    classe: 6,
    commentaire: 'Voir CNSS patronale.',
  },
  {
    role: 'charge_taxes_patronales',
    libelle: 'Taxes patronales (CFP, FNH, FGTLS)',
    sens: 'debit',
    compteDefaut: '664100',
    compteReferentielV2: '664',
    famille: '664',
    ecart: 'sous_compte',
    classe: 6,
    commentaire: 'Sous-compte du 664 : même famille.',
  },
  {
    role: 'dette_cnss',
    libelle: 'Organismes sociaux — CNSS (salarial + patronal)',
    sens: 'credit',
    compteDefaut: '4313',
    compteReferentielV2: '431',
    famille: '431',
    ecart: 'sous_compte',
    classe: 4,
    commentaire: 'Sous-compte du 431.',
  },
  {
    role: 'dette_cnamgs',
    libelle: 'Organismes sociaux — CNAMGS (salarial + patronal)',
    sens: 'credit',
    compteDefaut: '4314',
    compteReferentielV2: '431',
    famille: '431',
    ecart: 'sous_compte',
    classe: 4,
    commentaire: 'Sous-compte du 431.',
  },
  {
    role: 'dette_irpp',
    libelle: 'État — IRPP retenu à la source',
    sens: 'credit',
    compteDefaut: '4421',
    compteReferentielV2: '447',
    famille: '447',
    ecart: 'different',
    classe: 4,
    commentaire:
      "Le référentiel v2 range IRPP et TCS au 447 (impôts retenus à la source). Le code range l'IRPP au 4421. À valider.",
  },
  {
    role: 'dette_tcs',
    libelle: 'État — TCS retenue à la source',
    sens: 'credit',
    compteDefaut: '4473',
    compteReferentielV2: '447',
    famille: '447',
    ecart: 'sous_compte',
    classe: 4,
    commentaire: 'Sous-compte du 447.',
  },
  {
    role: 'dette_css',
    libelle: 'État — CSS retenue à la source',
    sens: 'credit',
    compteDefaut: '4474',
    compteReferentielV2: '447',
    famille: '447',
    ecart: 'sous_compte',
    classe: 4,
    commentaire:
      "Retenue de 1 % absente du référentiel v2 (DEC-05 : suppression proposée, à valider par l'expert-comptable).",
  },
  {
    role: 'dette_cfp',
    libelle: 'CFP à payer',
    sens: 'credit',
    compteDefaut: '4471',
    compteReferentielV2: '(non précisé)',
    famille: '447',
    ecart: 'a_preciser',
    classe: 4,
    commentaire:
      "Le référentiel v2 débite CFP et FNH au 664 mais ne dit pas où ils sont dus. Rattachés ici au 447 (taxes dues à l'État) : à préciser par l'expert-comptable.",
  },
  {
    role: 'dette_fnh',
    libelle: 'FNH à payer',
    sens: 'credit',
    compteDefaut: '4472',
    compteReferentielV2: '(non précisé)',
    famille: '447',
    ecart: 'a_preciser',
    classe: 4,
    commentaire: 'Voir CFP.',
  },
  {
    role: 'dette_fgtls',
    libelle: 'FGTLS à payer',
    sens: 'credit',
    compteDefaut: '4475',
    compteReferentielV2: '(non précisé)',
    famille: '447',
    ecart: 'a_preciser',
    classe: 4,
    commentaire: 'FGTLS 1 % absent du référentiel v2 (DEC-05 : à valider). Rattaché au 447 en attendant.',
  },
  {
    role: 'dette_net',
    libelle: 'Personnel — rémunérations dues (net à payer)',
    sens: 'credit',
    compteDefaut: '422000',
    compteReferentielV2: '421',
    famille: '421',
    ecart: 'different',
    classe: 4,
    commentaire:
      "POINT PRINCIPAL À VALIDER. Le code suit la nomenclature SYSCOHADA classique : 422 = rémunérations dues, 421 = avances et acomptes. Le référentiel v2 inverse (421 = net à payer), et le marque lui-même « à valider AUDCIF ». Si l'expert-comptable confirme la nomenclature classique, c'est le référentiel v2 qui doit être corrigé.",
  },
  {
    role: 'dette_avances',
    libelle: 'Personnel — avances et acomptes (déduits du net)',
    sens: 'credit',
    compteDefaut: '421000',
    compteReferentielV2: '425',
    famille: '425',
    ecart: 'different',
    classe: 4,
    commentaire:
      "Le référentiel v2 crédite les déductions au 425 ; le code crédite les avances (soldant l'avance versée) au 421. À valider.",
  },
];

export type TableComptesPaie = Readonly<Record<RoleComptePaie, string>>;
export type SurchargesComptesPaie = Partial<Record<RoleComptePaie, string>>;

function construireDefaut(): TableComptesPaie {
  const table = {} as Record<RoleComptePaie, string>;
  for (const l of CORRESPONDANCE_COMPTES_PAIE) table[l.role] = l.compteDefaut;
  return Object.freeze(table);
}

/** Valeurs par défaut documentées (option A). */
export const COMPTES_PAIE_PAR_DEFAUT: TableComptesPaie = construireDefaut();

export class ComptesPaieInvalideError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ComptesPaieInvalideError';
  }
}

const FORMAT_COMPTE = /^[1-9]\d{2,7}$/;

function ligneDuRole(role: string): LigneCorrespondanceComptePaie {
  const l = CORRESPONDANCE_COMPTES_PAIE.find((x) => x.role === role);
  if (!l) throw new ComptesPaieInvalideError(`Rôle de compte de paie inconnu : ${role}`);
  return l;
}

/** Valide une table complète (format, classe, ambiguïté de famille). Lève `ComptesPaieInvalideError`. */
export function validerTableComptesPaie(table: TableComptesPaie): void {
  const parCompte = new Map<string, FamilleReferentielV2>();
  for (const l of CORRESPONDANCE_COMPTES_PAIE) {
    const compte = table[l.role];
    if (typeof compte !== 'string' || !FORMAT_COMPTE.test(compte)) {
      throw new ComptesPaieInvalideError(
        `Compte invalide pour « ${l.libelle} » : « ${String(compte)} » (3 à 8 chiffres attendus).`,
      );
    }
    if (Number(compte[0]) !== l.classe) {
      throw new ComptesPaieInvalideError(
        `Compte ${compte} refusé pour « ${l.libelle} » : un compte de classe ${l.classe} est exigé.`,
      );
    }
    const deja = parCompte.get(compte);
    if (deja && deja !== l.famille) {
      throw new ComptesPaieInvalideError(
        `Compte ${compte} utilisé pour deux familles du référentiel (${deja} et ${l.famille}) : lecture ambiguë.`,
      );
    }
    parCompte.set(compte, l.famille);
  }
}

/**
 * Résout la table d'une entreprise : défauts + surcharges. Les rôles inconnus sont refusés
 * (faute de frappe = erreur visible, pas un compte silencieusement ignoré).
 */
export function resoudreComptesPaie(surcharges?: SurchargesComptesPaie | null): TableComptesPaie {
  const table: Record<RoleComptePaie, string> = { ...COMPTES_PAIE_PAR_DEFAUT };
  if (surcharges) {
    for (const [role, compte] of Object.entries(surcharges)) {
      if (compte === undefined) continue;
      ligneDuRole(role);
      table[role as RoleComptePaie] = String(compte).trim();
    }
  }
  validerTableComptesPaie(table);
  return Object.freeze(table);
}

/** Famille du référentiel v2 d'un compte, selon la table de l'entreprise (null si hors paie). */
export function familleReferentielDuCompte(
  compte: string,
  table: TableComptesPaie = COMPTES_PAIE_PAR_DEFAUT,
): FamilleReferentielV2 | null {
  for (const l of CORRESPONDANCE_COMPTES_PAIE) {
    if (table[l.role] === compte) return l.famille;
  }
  return null;
}

/** Ensemble des comptes utilisés par la table (comptes de paie reconnus pour la réconciliation). */
export function comptesDeLaTable(table: TableComptesPaie = COMPTES_PAIE_PAR_DEFAUT): ReadonlySet<string> {
  return new Set(ROLES_COMPTE_PAIE.map((r) => table[r]));
}

export type TotauxParFamille = Record<FamilleReferentielV2, { debit: number; credit: number }>;

/** Totaux débit/crédit par famille du référentiel v2 (lecture LIENS_PAIE). */
export function ventilerParFamille(
  lignes: readonly { compte: string; debit: number; credit: number }[],
  table: TableComptesPaie = COMPTES_PAIE_PAR_DEFAUT,
): TotauxParFamille {
  const out: TotauxParFamille = {
    '661': { debit: 0, credit: 0 },
    '664': { debit: 0, credit: 0 },
    '421': { debit: 0, credit: 0 },
    '425': { debit: 0, credit: 0 },
    '431': { debit: 0, credit: 0 },
    '447': { debit: 0, credit: 0 },
    hors_referentiel: { debit: 0, credit: 0 },
  };
  for (const l of lignes) {
    const famille = familleReferentielDuCompte(l.compte, table) ?? 'hors_referentiel';
    out[famille].debit += l.debit;
    out[famille].credit += l.credit;
  }
  return out;
}
